import { Component, DestroyRef, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
    AangeverBurgerlijkeStaat,
    Aangifte,
    AangifteSchenkbelasting,
    AllowanceEntity,
    BokkzClient,
    BtwOverzicht,
    ClientEntity,
    CorporateTaxEntity,
    Dividendbelasting,
    DividendTaxEntity,
    GiftTaxEntity,
    IncomeTaxEntity,
    IntermediaryEntity,
    Toeslagen,
    ValueAddedTaxEntity,
    Vermogensvergelijking,
    VpbAangifte,
} from '../services/api.bokkz.services';
import { FormControl, FormGroup } from '@angular/forms';
import { SelectFormControlItem, SharedUiFormControlsModule } from '@snelstart/ui-form-controls';
import { ToastService } from '@snelstart/ui-toast';
import {
    BokkzRapportageClient,
    FinancialStatementValue,
    Jaarrapportage,
    JaarrapportageEntity,
    Jaarrekening,
    ProfitAppropriationValue,
    WinstEnVerliesrekeningIndelingType,
} from '../services/api.bokkz.jaarstukken';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiAlertModule } from '@snelstart/ui-alert';
import { SharedUiDetailsModule } from '@snelstart/ui-details';
import { CommonModule } from '@angular/common';
import { BokkzAlgDataService } from '../services/bokkz-alg-data-service';
import { BokkzBtwDataService } from '../services/bokkz-btw-data-service';
import { filter, Observable, shareReplay } from 'rxjs';
import { BokkzVpbDataService } from '../services/bokkz-vpb-data-service';
import { BokkzIbDataService } from '../services/bokkz-ib-data-service';
import { BokkzDivDataService } from '../services/bokkz-div-data-service';
import { BokkzJrDataService } from '../services/bokkz-jr-data-service';
import { BokkzSbDataService } from '../services/bokkz-sb-data-service';
import { BokkzTslDataService } from '../services/bokkz-tsl-data-service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'snelstart-venture-fiscaal-domain-logic-service',
    templateUrl: './feature-service.component.html',
    styleUrl: './feature-service.component.css',
    imports: [
        CommonModule,
        SharedUiButtonModule,
        SharedUiDialogModule,
        SharedUiAlertModule,
        SharedUiDetailsModule,
        SharedUiFormControlsModule,
    ],
})
export class FeatureServiceComponent implements OnInit, OnDestroy {
    @Input() public dialogVisible = false;
    @Output() public dialogVisibleChange = new EventEmitter<boolean>();
    @Input() public title: any;
    @Output() created = new EventEmitter<void>();

    private readonly jaarrekeningRechtspersoonRechtsvormen = ['BV', 'NV', 'COOPERATIE'];
    private intermediaryEntity = {} as IntermediaryEntity;
    private clientEntity = {} as ClientEntity;
    private type: any;

    private destroyRef = inject(DestroyRef);
    private bokkzClient = inject(BokkzClient);
    private algDataService = inject(BokkzAlgDataService);
    private vpbDataService = inject(BokkzVpbDataService);
    private btwDataService = inject(BokkzBtwDataService);
    private jrDataService = inject(BokkzJrDataService);
    private ibDataService = inject(BokkzIbDataService);
    private divDataService = inject(BokkzDivDataService);
    private schenkDataService = inject(BokkzSbDataService);
    private tslDataService = inject(BokkzTslDataService);
    private bokkzRapportageClient = inject(BokkzRapportageClient);
    private readonly toastService = inject(ToastService);

    public formGroup = new FormGroup({
        name: new FormControl<string | null>(null),
        fiscalnumber: new FormControl<string | null>(null),
        type: new FormControl<string | null>(null),
        year: new FormControl<string | null>(null),
        biljet: new FormControl<string | null>(null),
        jrJaarrekeningType: new FormControl<string | null>(null),
        jrGrondslagType: new FormControl<string | null>(null),
        jrClassificatie: new FormControl<string | null>(null),
        jrRechtsvorm: new FormControl<string | null>(null),
        jrPlaats: new FormControl<string | null>(null),
        frequentie: new FormControl<string | null>(null),
        fiscaleEenheid: new FormControl<boolean | null>(null),
    });

    public optionsJaarrekeningType = [
        { value: 'ENKELVOUDIG', label: 'Enkelvoudige jaarrekening' },
        { value: 'GECONSOLIDEERD', label: 'Geconsolideerde jaarrekening', disabled: true },
    ];

    public optionsGrondslagType = [
        { value: 'COMMERCIEEL', label: 'Commercieel' },
        { value: 'FISCAAL', label: 'Fiscaal' },
    ];

    public optionsFrequentie: SelectFormControlItem<string>[] = [
        { value: 'MAAND', label: 'Maandaangifte' },
        { value: 'KWARTAAL', label: 'Kwartaalaangifte' },
        { value: 'JAAR', label: 'Jaaraangifte' },
    ];

    public optionsBiljetsoort: SelectFormControlItem<string>[] = [
        { value: 'P', label: 'P - Binnenlands belastingplichtige', disabled: false },
        { value: 'F', label: 'F - Overlijdensaangifte', disabled: false },
        { value: 'C', label: 'C - Buitenlands belastingplichtige', disabled: false },
        { value: 'M', label: 'M - Migrant', disabled: true },
    ];

    public optionsJaNee: SelectFormControlItem<boolean>[] = [
        { value: false, label: 'Nee' },
        { value: true, label: 'Ja' },
    ];

    public optionsBedrijfsklasse: SelectFormControlItem<string>[] = [
        { value: 'MICRO', label: 'Micro' },
        { value: 'KLEIN', label: 'Klein' },
        { value: 'MIDDELGROOT', label: 'Middelgroot', disabled: true },
        { value: 'GROOT', label: 'Groot', disabled: true },
    ];

    public optionsRechtsvorm: SelectFormControlItem<string>[] = [
        { value: 'BV', label: 'Besloten vennootschap met beperkte aansprakelijkheid' },
        { value: 'NV', label: 'Naamloze vennootschap' },
        { value: 'COOPERATIE', label: 'Coöperatie' },
        { value: 'EENMANSZAAK', label: 'Eenmanszaak' },
        { value: 'VOF', label: 'Vennootschap onder firma' },
        { value: 'MAATSCHAP', label: 'Maatschap' },
        { value: 'CV', label: 'Commanditaire vennootschap' },
    ];

    readonly intermediaryEntity$: Observable<IncomeTaxEntity> = this.algDataService.intermediaryEntity$.pipe(
        filter((data): data is IncomeTaxEntity => !!data?.sstPrimaryAdminId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly clientEntity$: Observable<ClientEntity> = this.algDataService.clientEntity$.pipe(
        filter((data): data is ClientEntity => !!data?.sstRelationId),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly selectedYear$: Observable<number> = this.algDataService.selectedYear$.pipe(
        filter((data): data is number => !!data),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    readonly selectedService$: Observable<string> = this.algDataService.selectedService$.pipe(
        filter((data): data is string => !!data),
        takeUntilDestroyed(this.destroyRef),
        shareReplay({ bufferSize: 1, refCount: true }),
    );

    ngOnInit(): void {
        this.intermediaryEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.intermediaryEntity = value;
        });
        this.clientEntity$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.clientEntity = value;
            this.formGroup.patchValue({ name: value.name, jrPlaats: value.place }, { emitEvent: false });
        });
        this.selectedYear$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            this.formGroup.patchValue({ year: String(value) }, { emitEvent: false });
        });
        this.selectedService$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((type) => {
            this.type = type;

            let fiscalnumber = '';
            if (type.startsWith('BTW') && this.clientEntity?.taxIdVat) {
                fiscalnumber = this.clientEntity.taxIdVat;
            } else if (type.startsWith('JR') && this.clientEntity?.kvkId) {
                fiscalnumber = this.clientEntity.kvkId;
            } else if (this.clientEntity?.taxId) {
                fiscalnumber = this.clientEntity.taxId;
            }

            this.formGroup.patchValue({ type: type }, { emitEvent: false });
            this.formGroup.patchValue({ fiscalnumber: fiscalnumber }, { emitEvent: false });

            if (type.startsWith('JR')) {
                if (this.clientEntity?.classificatie) {
                    this.formGroup.patchValue({ jrClassificatie: this.clientEntity.classificatie }, { emitEvent: false });
                }
                this.formGroup.patchValue({ jrJaarrekeningType: 'ENKELVOUDIG' }, { emitEvent: false });
            }
        });
    }

    ngOnDestroy(): void {
        // rely on SaveForm
    }

    get jaarrekeningRechtsvormRechtspersoon(): boolean {
        return this.jaarrekeningRechtspersoonRechtsvormen.includes(this.formGroup.get('jrRechtsvorm')?.value ?? '');
    }

    public saveDisabled(): boolean {
        if (this.type === 'JR') {
            const jrJaarrekeningType = this.formGroup.value.jrJaarrekeningType;
            const jrGrondslagType = this.formGroup.value.jrGrondslagType;
            const jrClassificatie = this.formGroup.getRawValue().jrClassificatie;
            const jrRechtsvorm = this.formGroup.getRawValue().jrRechtsvorm;
            const fiscalnumber = this.formGroup.getRawValue().fiscalnumber;
            if (!jrJaarrekeningType || !jrGrondslagType || !jrClassificatie || !jrRechtsvorm || !fiscalnumber) {
                return true;
            }
        }
        if (this.type === 'IB') {
            const biljet = this.formGroup.value.biljet;
            if (!biljet) {
                return true;
            }
        }
        return false;
    }

    public saveForm() {
        const type = this.formGroup.getRawValue().type;
        const year = this.formGroup.getRawValue().year;
        const fiscalnumber = this.formGroup.getRawValue().fiscalnumber;
        const jrJaarrekeningType = this.formGroup.value.jrJaarrekeningType;
        const jrGrondslagType = this.formGroup.value.jrGrondslagType;
        const jrClassificatie = this.formGroup.getRawValue().jrClassificatie;
        const jrRechtsvorm = this.formGroup.value.jrRechtsvorm;
        const jrPlaats = this.clientEntity.place;
        const fe = this.formGroup.value.fiscaleEenheid;
        if (type === 'VPB') {
            const newAangifte = {
                biljetSoort: 'A',
                boekjaar: {
                    begindatum: new Date(year + '-01-01'),
                    einddatum: new Date(year + '-12-31'),
                },
                identificatie: {
                    naamLichaam: this.clientEntity.name,
                    fiscaalNummer: fiscalnumber,
                    beconnummer: this.intermediaryEntity.beconId,
                    achternaamOndertekenaar: this.intermediaryEntity.officeName,
                    aangifteFiscaleEenheid: fe,
                },
                algemeneVragen: {
                    verzoekExplicietStandpunt: false,
                    regelingFunctioneleValuta: false,
                    gebruikInnovatiebox: false,
                    internationaleHoustervennootschap: false,
                    toepassingBesluitBeleggingsinstellingen: false,
                    toepassingBesluitWinstbepalingVerzekeraars: false,
                    activiteitenOndernemingGewijzigd: false,
                    wijzigingWaarderingsstelsel: false,
                    willekeurigAfschijven: false,
                    nieuweOfGewijzigdePensioenregeling: false,
                    pensioenaanspraakVerminderd: false,
                    vervreemdingAfgewaardeerdeVordering: false,
                    aftrekValutaverliesOpDeelnemingen: false,
                    verdelingAandelenkapitaalGewijzigd: false,
                    beperkingVerliesverrekeningInOverdrachtsjaar: false,
                    earnOutRegeling: false,
                    toepassingTegenbewijsRenteaftrek: false,
                    schulden10aBuitenFiscaleEenheid: false,
                    compenserendeHeffing10aSchulden: false,
                    kennisCompenserende10aHeffingOntvanger: false,
                    saldoVorderingenEnSchuldenVerbondenLichamen: 0,
                    kwalificatieBelastbareWinst: false,
                    betaaldBedragVoorActiva: 0,
                    rechtshandelingenMetBuitenlandseAandeelhouders: false,
                    lokaalDossierBedrijfsreorganisatieOfAfdrachten: false,
                    belangInFiscaalTransparanteEntiteit: false,
                    vermogenAfgezonderdInApv: false,
                },
                balansEnResultatenrekening: {
                    naamVennootschap: this.clientEntity.name,
                    fiscaalNummer: fiscalnumber,
                    boekjaarVennootschap: {
                        begindatum: new Date(year + '-01-01'),
                        einddatum: new Date(year + '-12-31'),
                    },
                    dochtermaatschappijFiscaleEenheid: false,
                    agrarischeActiviteiten: false,
                },
                vermogensvergelijking: {} as Vermogensvergelijking,
            } as VpbAangifte;
            const entity = {
                name: this.clientEntity.name,
                taxPayerFiscalNumber: fiscalnumber,
                sstRelationId: this.clientEntity.sstRelationId,
                year: Number(year),
                aangifte: newAangifte,
            } as CorporateTaxEntity;
            this.bokkzClient.postCorporateTax(this.clientEntity.sstRelationId!, false, false, entity).subscribe({
                next: (response) => {
                    if (response.aangifte) {
                        this.vpbDataService.updateAangifte(response.aangifte);
                        this.vpbDataService.updateCorporateTaxEntity(response);
                    }
                },
                error: (err) => {
                    this.dialogVisibleChange.emit(false);
                    console.log(err);
                    this.toastService.showErrorMessage('Geen nieuwe aangifte toegevoegd.');
                },
                complete: () => this.dialogVisibleChange.emit(false),
            });
        }
        if (type === 'DB') {
            const newAangifte = {
                dienstverlener: {
                    naamDienstverlener: this.intermediaryEntity.officeName,
                    rsinDienstverlener: this.intermediaryEntity.taxId,
                    beconNummer: this.intermediaryEntity.beconId,
                },
                inhoudingsplichtige: {
                    rsinInhoudingsplichtige: this.clientEntity.taxId,
                    naamInhoudingsplichtige: this.clientEntity.name,
                },
            } as Dividendbelasting;
            const entity = {
                name: this.clientEntity.name,
                taxPayerFiscalNumber: this.clientEntity.taxId,
                sstRelationId: this.clientEntity.sstRelationId,
                year: Number(year),
                aangifte: newAangifte,
            } as DividendTaxEntity;
            this.bokkzClient.postDividendTax(this.clientEntity.sstRelationId!, false, entity).subscribe({
                next: (response) => {
                    if (response && response.aangifte != null) {
                        this.divDataService.updateDividendTaxEntity(response);
                    }
                },
                error: (err) => {
                    this.dialogVisibleChange.emit(false);
                    console.log(err);
                    this.toastService.showErrorMessage('Geen nieuwe aangifte toegevoegd.');
                },
                complete: () => this.dialogVisibleChange.emit(false),
            });
        }
        if (type === 'IB') {
            const newAangifte = {
                biljetSoort: this.formGroup.value.biljet ?? undefined,
                aangifteJaar: Number(year),
                aangever: {
                    voorletters: this.clientEntity.voorletters ?? undefined,
                    tussenvoegsels: this.clientEntity.tussenvoegsel !== undefined ? this.clientEntity.tussenvoegsel : undefined,
                    achternaam: this.clientEntity.achternaam ?? undefined,
                    bsn: this.clientEntity.taxId ?? undefined,
                    geboortedatum: this.clientEntity.geboortedatum ?? undefined,
                    burgerlijkeStaat: AangeverBurgerlijkeStaat.ALLEENSTAAND ?? undefined,
                },
                vertegenwoordiger: {
                    beconnummer: this.intermediaryEntity.beconId ?? undefined,
                    naamDienstVerlener: this.intermediaryEntity.officeName ?? undefined,
                },
            } as Aangifte;
            const entity = {
                name: this.clientEntity.name ?? undefined,
                taxPayerFiscalNumber: this.clientEntity.taxId ?? undefined,
                sstRelationId: this.clientEntity.sstRelationId ?? undefined,
                year: Number(year),
                aangifte: newAangifte,
            } as IncomeTaxEntity;
            this.bokkzClient.postIncomeTax(this.clientEntity.sstRelationId!, false, false, false, false, entity).subscribe({
                next: (response) => {
                    if (response && response.aangifte != null) {
                        this.ibDataService.updateIncomeTaxEntity(response);
                        this.ibDataService.createMenuInterface(response);
                        this.created.emit();
                    }
                },
                error: (err) => {
                    console.log(err);
                    this.toastService.showErrorMessage('Geen nieuwe aangifte toegevoegd.');
                    this.dialogVisibleChange.emit(false);
                },
                complete: () => {
                    this.dialogVisibleChange.emit(false);
                },
            });
        }
        if (type == 'TSL') {
            const newToeslagen = {
                berekeningsjaar: Number(year),
                bsn: this.clientEntity.taxId,
            } as Toeslagen;
            const newAllowanceEntity = {
                name: this.clientEntity.name,
                taxPayerFiscalNumber: this.clientEntity.taxId,
                sstRelationId: this.clientEntity.sstRelationId,
                year: Number(year),
                toeslagen: newToeslagen,
            } as AllowanceEntity;
            this.bokkzClient.postAllowance(this.clientEntity.sstRelationId!, false, newAllowanceEntity).subscribe({
                next: (response) => {
                    if (response && response.year != null) {
                        this.created.emit();
                        this.tslDataService.updateAllowanceTaxEntity(response);
                        this.tslDataService.createMenuInterface();
                    }
                },
                error: (err) => {
                    this.dialogVisibleChange.emit(false);
                    console.log(err);
                    this.toastService.showErrorMessage('Geen nieuwe aangifte toegevoegd.');
                },
                complete: () => this.dialogVisibleChange.emit(false),
            });
        }
        if (type === 'BTW') {
            const newAangifte = {
                jaar: Number(year),
                achternaamBelastingplichtige: this.clientEntity.name,
                omzetbelastingNummer: this.clientEntity.taxIdVat,
                beconNummer: this.intermediaryEntity.beconId,
                naamContactPersoon: this.intermediaryEntity.officeName,
                frequentie: this.formGroup.value.frequentie,
                fiscaleEenheid: false,
            } as BtwOverzicht;
            const newEntity = {
                name: this.clientEntity.name,
                sstRelationId: this.clientEntity.sstRelationId,
                taxPayerFiscalNumber: fiscalnumber,
                year: Number(year),
                overzicht: newAangifte,
            } as ValueAddedTaxEntity;
            this.bokkzClient.postValueAddedTax(this.clientEntity.sstRelationId!, false, newEntity).subscribe({
                next: (response) => {
                    if (response && response.year != null) {
                        this.btwDataService.valueAddedTaxEntity.next(response);
                        this.btwDataService.calculateLatestValueAddedTaxEntity().subscribe((data) => {
                            response.overzicht = data;
                            this.btwDataService.updateValueAddedTaxEntity(response);
                        });
                    }
                },
                error: (err) => {
                    this.dialogVisibleChange.emit(false);
                    console.log(err);
                    this.toastService.showErrorMessage('Geen nieuwe aangifte toegevoegd.');
                },
                complete: () => this.dialogVisibleChange.emit(false),
            });
        }
        if (type === 'SB') {
            const newAangifte = {
                schenker: {
                    voorletters: this.clientEntity.voorletters,
                    tussenvoegsels: this.clientEntity.tussenvoegsel,
                    achternaam: this.clientEntity.achternaam,
                    bsn: this.clientEntity.taxId,
                },
                aangever: {
                    voorletters: this.clientEntity.voorletters,
                    tussenvoegsels: this.clientEntity.tussenvoegsel,
                    achternaam: this.clientEntity.achternaam,
                    bsn: this.clientEntity.taxId,
                },
            } as AangifteSchenkbelasting;
            const newEntity = {
                year: Number(year),
                taxPayerFiscalNumber: this.clientEntity.taxId,
                sstRelationId: this.clientEntity.sstRelationId,
                aangifte: newAangifte,
                lastOpeningDate: new Date(),
            } as GiftTaxEntity;
            this.bokkzClient.postGiftTax(this.clientEntity.sstRelationId!, false, newEntity).subscribe({
                next: (response) => {
                    if (response && response.year != null) {
                        this.schenkDataService.updateGiftTaxEntity(response);
                    }
                },
                error: (err) => {
                    this.dialogVisibleChange.emit(false);
                    console.log(err);
                    this.toastService.showErrorMessage('Geen nieuwe aangifte toegevoegd.');
                },
                complete: () => this.dialogVisibleChange.emit(false),
            });
        }
        if (type === 'JR') {
            const jaarrekening = {
                jaarrekeningType: jrJaarrekeningType,
                balans: {
                    balanceSheetBeforeAfterAppropriationResults: {
                        value: ProfitAppropriationValue.VOOR,
                    },
                },
                winstEnVerliesrekening: {
                    winstEnVerliesrekeningIndelingType: WinstEnVerliesrekeningIndelingType.BEDRIJFSOPBRENGSTEN_BEDRIJFSLASTEN,
                },
            } as Jaarrekening;
            const newJaarrapport = {
                informatieEntiteit: {
                    legalEntityName: { value: this.clientEntity.name },
                    businessNames: { value: this.clientEntity.name },
                    chamberOfCommerceRegistrationNumber: { value: this.clientEntity.kvkId },
                    legalSizeCriteriaClassification: { value: jrClassificatie },
                    legalEntityLegalForm: { value: jrRechtsvorm },
                    legalEntityRegisteredOffice: { value: jrPlaats },
                    sbiBusinessCode: { value: this.clientEntity.sbiCode },
                    adres: {
                        streetNameNL: { value: this.clientEntity.street },
                        houseNumberNL: { value: this.clientEntity.houseNumber },
                        houseNumberAddition: { value: this.clientEntity.numberAddition },
                        postalCodeNL: { value: this.clientEntity.postalCode },
                        placeOfResidenceNL: { value: this.clientEntity.place },
                    },
                },
                informatieRapportage: {
                    titleOfTheDocument: { value: 'Jaarrapportage ' + year },
                    financialReportingPeriodCurrentStartDate: {
                        value: new Date(year + '-01-01'),
                    },
                    financialReportingPeriodCurrentEndDate: {
                        value: new Date(year + '-12-31'),
                    },
                    documentRelatesToIndividualEntityOrGroup: {
                        value: FinancialStatementValue.ENKELVOUDIG,
                    },
                    documentPresentationCurrency: { value: 'EUR' },
                    basisOfPreparation: { value: jrGrondslagType },
                },
                jaarrekeningen: [jaarrekening],
            } as Jaarrapportage;
            const newEntity = {
                year: Number(year),
                rapportage: newJaarrapport,
                dateCreation: new Date(),
            } as JaarrapportageEntity;
            if (this.clientEntity.sstRelationId) {
                this.bokkzRapportageClient
                    .postjaarrapportage(this.clientEntity.sstRelationId, String(newEntity.year), false, undefined, newEntity)
                    .subscribe({
                        next: (response) => {
                            if (response && response.year != null) {
                                this.jrDataService.updateEntity(response);
                            }
                        },
                        error: (err) => {
                            this.dialogVisibleChange.emit(false);
                            console.log(err);
                            this.toastService.showErrorMessage('Geen nieuwe rapportage toegevoegd.');
                        },
                        complete: () => this.dialogVisibleChange.emit(false),
                    });
            }
        }
    }
}
